import type { PathImages, PathImagesFilters } from '@cobliteam/api-lib/src/dashboard/camera/definitions/PathImages';
import { FaceIdRequester } from '@cobliteam/api-lib/src/dashboard/camera/FaceIdRequester';
import type { DriverFaceImageAssignRequest, PageableContent, UUID } from '@cobliteam/common-types';
import { ImageAssignRequestSource } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import type { QueryClient } from '@tanstack/react-query';
import { keepPreviousData, type UseQueryOptions, useQuery } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();
const faceIdRequester = new FaceIdRequester({ host: env.herbieApiHost });

const useGetImages = (vehicleId: UUID, filters: PathImagesFilters, options?: Partial<UseQueryOptions<PageableContent<PathImages>>>) => {
  return useQuery({
    queryKey: ['images', vehicleId, filters],
    queryFn: () => faceIdRequester.getImages(vehicleId, filters),
    placeholderData: keepPreviousData,
    ...options,
  });
};

interface PathLike {
  startTime: number;
  endTime: number;
  vehicle?: { id?: string | null } | null;
}

function buildImageAssignRequests(queryClient: QueryClient, paths: PathLike[], driverId: string): DriverFaceImageAssignRequest[] {
  return paths.flatMap((path) => {
    const cachedImages = queryClient.getQueryData<PageableContent<PathImages>>([
      'images',
      path.vehicle?.id || '',
      {
        fromTimestamp: new Date(path.startTime).toISOString(),
        toTimestamp: new Date(path.endTime).toISOString(),
        page: 0,
        size: 100,
      },
    ]);
    return (cachedImages?.content || []).map((image) => ({
      url: image.url,
      driverId,
      source: ImageAssignRequestSource.PATH_REVIEW,
    }));
  });
}

export { useGetImages, buildImageAssignRequests };
